import './PatientFlags.scss';

import * as React from 'react';

import { Action, Dispatch, bindActionCreators } from 'redux';
import { Checkbox, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, TextField, Label, IconButton } from '@fluentui/react';
import { UpdatePatientTagsRequest } from '../../redux/patient/types';
import { createNote, fetchNotes, updatePatientTags } from '../../redux/patient-notes/actions';

import { AsyncData } from '../../redux/core';
import { RootState } from '../../redux/types';
import { connect } from 'react-redux';
import { PatientNote, PatientNoteContent, PatientNoteFlag } from '../../api/patients.types';

const FLAGS: { [key: string]: string } = {
  'Declined Attribution': '#ffd4e4',
  'Bed Bound': '#ffd4e4',
  Deceased: '#ffd4e4',
  'Primary Medicaid': '#ffd4e4',
  'Breach of Community Guidelines': '#ffd4e4',
  'Unfit for House Calls': '#ffd4e4',
  'Allow Unattributed Booking': '#ffd4e4',
  'Engagement Patient': '#cceefd',
  'Patient is in Hospice Care': '#cceefd',
};

export interface OwnProps {
  patientId: string;
  visitCode?: string;
  label?: string;
}

export interface StateProps {
  notes?: AsyncData<PatientNote[]>;
}

export interface DispatchProps {
  actions: {
    fetchNotes: (patientId: string) => void;
    createNote: (patientId: string, note: string, visitCode?: string, operationsOnly?: boolean) => void;
    updatePatientTags: (patientId: string, updateRequest: UpdatePatientTagsRequest) => void;
  };
}

type PatientFlagsProps = OwnProps & StateProps & DispatchProps;

export interface PatientFlagsState {
  isSaving: boolean;
  hideFlagsDialog: boolean;
  originalFlags: string[];
  flags: string[];
  comment: string;
}

class Component extends React.Component<PatientFlagsProps, PatientFlagsState> {
  constructor(props: PatientFlagsProps) {
    super(props);
    this.state = {
      isSaving: false,
      hideFlagsDialog: true,
      comment: '',
      flags: [],
      originalFlags: [],
    };
  }

  public componentDidMount() {
    this.props.actions.fetchNotes(this.props.patientId);
  }

  public componentDidUpdate(prevProps: PatientFlagsProps) {
    if (this.props.patientId !== prevProps.patientId) {
      this.props.actions.fetchNotes(this.props.patientId);
    }
    if (this.state.isSaving && this.props !== prevProps) {
      this.setState({ isSaving: false, comment: '', hideFlagsDialog: true });
    }

    if (this.props !== prevProps && this.props.notes?.data) {
      const flags = this.getFlagsFromNote(this.props.notes.data);
      const originalFlags = flags.slice();
      this.setState({ originalFlags, flags });
    }
  }

  public render(): JSX.Element {
    const { originalFlags } = this.state;

    return (
      <div className="flag-container flex">
        {this.props.label && <Label>{this.props.label}&nbsp;</Label>}
        {originalFlags.map((s, i) => (
          <span className="flag-item" style={{ backgroundColor: FLAGS[s] }} key={i}>
            {s}
          </span>
        ))}
        <IconButton iconProps={{ iconName: 'Edit' }} onClick={this.onEditClick} />
        {this.renderDialog()}
      </div>
    );
  }

  private renderDialog(): JSX.Element | null {
    if (this.state.hideFlagsDialog) {
      return null;
    }

    return (
      <Dialog
        hidden={false}
        onDismiss={() => this.setState({ hideFlagsDialog: true })}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Manage Flags',
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        {Object.keys(FLAGS).map((f, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <Checkbox label={f} checked={this.state.flags.indexOf(f) >= 0} onChange={(ev, checked) => this.onFlagChange(f, checked!)} />
          </div>
        ))}
        <TextField
          multiline={true}
          rows={3}
          required={true}
          placeholder="Add justification for changing the flags"
          onChange={(evt, value) => this.setState({ comment: value! })}
          value={this.state.comment}
        />
        <DialogFooter>
          <PrimaryButton text="Save" disabled={this.state.comment.length < 4} onClick={this.onSave} />
          <DefaultButton text="Cancel" onClick={() => this.setState({ hideFlagsDialog: true })} />
        </DialogFooter>
      </Dialog>
    );
  }

  private onEditClick = () => {
    this.setState({
      hideFlagsDialog: false,
      flags: this.state.originalFlags.slice(),
      comment: '',
    });
  };

  private onFlagChange(flag: string, checked: boolean) {
    if (checked) {
      const flags = this.state.flags.slice();
      flags.push(flag);
      this.setState({ flags });
    } else {
      const index = this.state.flags.indexOf(flag);
      if (index > -1) {
        const flags = this.state.flags.slice();
        flags.splice(index, 1);
        this.setState({ flags });
      }
    }
  }

  private onSave = () => {
    const updateReq: UpdatePatientTagsRequest = {
      comment: this.state.comment,
      tags: {
        tagNames: this.state.flags,
      },
      relatedVisitCode: this.props.visitCode,
    };

    this.updateTags(updateReq);
  };

  private updateTags(req: UpdatePatientTagsRequest) {
    this.setState({ isSaving: true });
    this.props.actions.updatePatientTags(this.props.patientId, req);
  }

  private getFlagsFromNote(notes: PatientNote[]): string[] {
    let flagData!: PatientNoteFlag;
    notes.find((n) => {
      try {
        const content = JSON.parse(n.note) as PatientNoteContent;
        if (content.kind === 'flag') {
          flagData = content;
          return true;
        }
      } catch (e: any) {
        /*continue*/
      }
      return false;
    });

    return flagData ? flagData.flags : [];
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  return {
    notes: state.patientNotes.data[ownProps.patientId],
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
  return {
    actions: bindActionCreators({ fetchNotes, createNote, updatePatientTags }, dispatch),
  };
};

export const PatientFlags = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component);
